import { useState, useEffect } from "react";
import {
  timetables,
  subjectColors,
  DAY_LABELS,
  DAYS,
  PERIODS,
  CLASSES,
  type DayKey,
  type ClassKey,
} from "@/mocks/timetable";
import JuicyButton from "@/components/base/JuicyButton";

function getTodayKey(): DayKey {
  const day = new Date().getDay();
  const map: Record<number, DayKey> = { 1: "A", 2: "B", 3: "C", 4: "D", 5: "E" };
  return map[day] ?? "A";
}

export default function TimetableViewer() {
  const [selectedClass, setSelectedClass] = useState<ClassKey>("6組");
  const [selectedDay, setSelectedDay] = useState<DayKey>(getTodayKey());
  const [viewMode, setViewMode] = useState<"day" | "week">("day");

  useEffect(() => {
    setSelectedDay(getTodayKey());
  }, []);

  const data = timetables[selectedClass];

  return (
    <section id="timetable" className="py-24 px-6 bg-white">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-400 mb-3">時間割を確認</p>
          <h2 className="text-3xl md:text-4xl font-black text-black leading-tight">
            クラスと曜日を選ぶだけ。
          </h2>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          {/* Class selector */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-400 tracking-wide">クラス</label>
            <div className="flex gap-2 flex-wrap">
              {CLASSES.map((cls) => (
                <JuicyButton
                  key={cls}
                  onClick={() => setSelectedClass(cls)}
                  particleColor={selectedClass === cls ? "#ffffff" : "#000000"}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors duration-150 cursor-pointer whitespace-nowrap ${
                    selectedClass === cls
                      ? "bg-black text-white border-black"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                  }`}
                >
                  {cls}
                </JuicyButton>
              ))}
            </div>
          </div>

          {/* View mode */}
          <div className="flex flex-col gap-1.5 sm:ml-auto">
            <label className="text-xs font-medium text-gray-400 tracking-wide">表示</label>
            <div className="flex gap-1 bg-gray-100 rounded-full p-1">
              <JuicyButton
                onClick={() => setViewMode("day")}
                particleColor="#000000"
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  viewMode === "day" ? "bg-white text-black" : "text-gray-500 hover:text-black"
                }`}
              >
                1日
              </JuicyButton>
              <JuicyButton
                onClick={() => setViewMode("week")}
                particleColor="#000000"
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  viewMode === "week" ? "bg-white text-black" : "text-gray-500 hover:text-black"
                }`}
              >
                週間
              </JuicyButton>
            </div>
          </div>
        </div>

        {/* Day selector (only in day mode) */}
        {viewMode === "day" && (
          <div className="flex gap-2 mb-8 flex-wrap">
            {DAYS.map((day) => (
              <JuicyButton
                key={day}
                onClick={() => setSelectedDay(day)}
                particleColor={selectedDay === day ? "#ffffff" : "#000000"}
                className={`flex flex-col items-center px-5 py-3 rounded-2xl border transition-colors duration-150 cursor-pointer min-w-16 ${
                  selectedDay === day
                    ? "bg-black text-white border-black"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                }`}
              >
                <span className="text-xs font-medium opacity-60">{day}</span>
                <span className="text-sm font-bold mt-0.5">{DAY_LABELS[day].replace("曜日", "")}</span>
              </JuicyButton>
            ))}
          </div>
        )}

        {/* Day view */}
        {viewMode === "day" && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-bold text-black">{DAY_LABELS[selectedDay]}</h3>
              <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">{selectedClass}</span>
            </div>
            {PERIODS.map((period) => {
              const subject = data[selectedDay][period];
              const colorClass = subjectColors[subject] ?? "bg-gray-50 text-gray-600 border-gray-200";
              return (
                <div
                  key={period}
                  className={`flex items-center gap-5 px-6 py-4 rounded-2xl border transition-all duration-150 ${colorClass}`}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-black/5 flex items-center justify-center">
                    <span className="text-xs font-bold text-current opacity-60">{period}</span>
                  </div>
                  <div className="flex-1">
                    <span className="font-semibold text-base">{subject === "-" ? "授業なし" : subject}</span>
                  </div>
                  <span className="text-xs opacity-50">{period}時限目</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Week view */}
        {viewMode === "week" && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] border-collapse">
              <thead>
                <tr>
                  <th className="w-12 pb-3 text-xs font-medium text-gray-400 text-center">時限</th>
                  {DAYS.map((day) => (
                    <th key={day} className="pb-3 text-center">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-xs text-gray-400">{day}</span>
                        <span className="text-sm font-bold text-black">{DAY_LABELS[day].replace("曜日", "")}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERIODS.map((period) => (
                  <tr key={period}>
                    <td className="py-1.5 text-center">
                      <span className="text-xs font-bold text-gray-400">{period}</span>
                    </td>
                    {DAYS.map((day) => {
                      const subject = data[day][period];
                      const colorClass = subjectColors[subject] ?? "bg-gray-50 text-gray-600 border-gray-200";
                      return (
                        <td key={day} className="py-1.5 px-1">
                          <div className={`rounded-xl border px-2 py-2.5 text-center text-xs font-semibold ${colorClass}`}>
                            {subject === "-" ? <span className="opacity-30">—</span> : subject}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Legend */}
            <div className="mt-6 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-400 mr-1">科目カラー:</span>
              {[
                { label: "国語", cls: "bg-red-50 text-red-700 border-red-200" },
                { label: "数学", cls: "bg-sky-50 text-sky-700 border-sky-200" },
                { label: "英語", cls: "bg-violet-50 text-violet-700 border-violet-200" },
                { label: "理科", cls: "bg-teal-50 text-teal-700 border-teal-200" },
                { label: "社会", cls: "bg-orange-50 text-orange-700 border-orange-200" },
                { label: "保体", cls: "bg-green-50 text-green-700 border-green-200" },
                { label: "音楽", cls: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" },
                { label: "美術", cls: "bg-pink-50 text-pink-700 border-pink-200" },
              ].map((item) => (
                <span key={item.label} className={`text-xs px-2.5 py-1 rounded-full border font-medium ${item.cls}`}>
                  {item.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
